import { Injector, Provider, InjectionToken } from '@angular/core';
import {
  ErrorMessageResponse,
  hasWsErrorAttr,
  WebsocketEntTypes,
  WebsocketOpTypes,
} from '@qtek/core/websockets-core';

/**
 * Criteria to match a WS error frame against. All provided fields must match;
 * omitted fields are ignored.
 */
export interface WsErrorMatcher {
  ent?: WebsocketEntTypes;
  op?: WebsocketOpTypes;
  /** Key expected inside `sts.attrs`, e.g. `'not-found'`. */
  key?: string;
}

/**
 * A rule the global WS error handler consults before opening the default
 * error dialog. When `match` returns true, `handle` runs instead of the
 * dialog — omit `handle` to silently swallow the error.
 */
export interface WsErrorRule {
  match: (message: ErrorMessageResponse, injector: Injector) => boolean;
  handle?: (message: ErrorMessageResponse, injector: Injector) => void;
}

/** Multi-provided; `WsErrorHandlerService` reads every registered rule. */
export const WS_ERROR_RULES = new InjectionToken<WsErrorRule[]>(
  'WS_ERROR_RULES'
);

export function matchesWsError(
  message: ErrorMessageResponse,
  criteria: WsErrorMatcher
): boolean {
  if (criteria.ent !== undefined && message.ent !== criteria.ent) {
    return false;
  }
  if (criteria.op !== undefined && message.op !== criteria.op) {
    return false;
  }
  if (criteria.key !== undefined && !hasWsErrorAttr(message, criteria.key)) {
    return false;
  }
  return true;
}

/** A rule that silently swallows errors matching `criteria` (no dialog). */
export function ignoreWsError(criteria: WsErrorMatcher): WsErrorRule {
  return {
    match: message => matchesWsError(message, criteria),
  };
}

/** A rule that runs `handle` instead of the default dialog. */
export function handleWsError(
  criteria: WsErrorMatcher,
  handle: (message: ErrorMessageResponse, injector: Injector) => void
): WsErrorRule {
  return {
    match: message => matchesWsError(message, criteria),
    handle,
  };
}

/** Registers one or more rules with `WS_ERROR_RULES`. Add to an app's `providers`. */
export function provideWsErrorRules(...rules: WsErrorRule[]): Provider[] {
  return rules.map(rule => ({
    provide: WS_ERROR_RULES,
    useValue: rule,
    multi: true,
  }));
}
